import { useCallback } from 'react';
import ErrorHelpers from '~/app/errorHelpers.js';
import { PRINT_TYPES } from '~/app/constants';

/**
 * useMenuCommand
 *
 * Owns the handleMenuCommand handler that was previously a class method on
 * FormViewer. This is called by useSocketMessage (via socketCallbacksRef)
 * when the server sends a 'return_menu' command.
 *
 * Commands handled:
 *   refundCalcCreate — legacy refund-calc save (DOM path, kept for compatibility)
 *   refundCalcExit   — legacy: sets showExitDialog state (dead state, kept)
 *   stockImport      — open import-stock modal
 *   print199a        — print STMT 199A
 *   prt1042S         — print RRB 1042-S sign declaration
 *   prev1040         — print 1040 preview
 *   resetUST1        — reload UST101 form
 *
 * Returns:
 *   { handleMenuCommand }
 */
const useMenuCommand = ({
  calcAPIRef,
  isReadOnlyRef, // kept-current ref to props.isReadOnly
  // Hook results (kept-current via the wrapper's ref pattern)
  printRef, // kept-current ref to usePrint result
  importStockRef, // kept-current ref to useImportStock result
}) => {
  const handleMenuCommand = useCallback(
    (cmd, resp = null) => {
      try {
        switch (cmd) {
          case 'refundCalcCreate':
            // Legacy path — DOM helper was removed; retained as no-op for compatibility
            break;
          case 'refundCalcExit':
            // Legacy path — showExitDialog state was removed; no-op for compatibility
            break;
          case 'stockImport':
            importStockRef.current.openImportStock(resp.arg);
            break;
          case 'print199a':
            printRef.current.handlePrint(PRINT_TYPES.STMT199A, {}, 'STMT 199A');
            break;
          case 'prt1042S':
            printRef.current.handlePrint(PRINT_TYPES.RRB_1042S_SIGN_DOC, {}, 'Sign Declaration');
            break;
          case 'prev1040':
            // Call return_print directly instead of going through return_print_query
            // to mimic behavior with desktop
            calcAPIRef.current.ReturnPrint(
              PRINT_TYPES.PREVIEW_1040,
              {},
              '',
              {},
              PRINT_TYPES.PREVIEW_1040,
              '',
              '',
              isReadOnlyRef.current,
              resp?.arg?.PKG,
            );
            break;
          case 'resetUST1':
            calcAPIRef.current.ReturnFormLoad('UST101', '', '', '');
            break;
          default:
            throw ErrorHelpers.createSimpleError('Unable to complete action');
        }
      } catch (error) {
        ErrorHelpers.handleError('Unable to complete action', error);
      }
    },
    [calcAPIRef, importStockRef, isReadOnlyRef, printRef],
  );

  return { handleMenuCommand };
};

export default useMenuCommand;
